/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { EamModelImpl } from "./eam";
import { PushNotificationType } from "./types";
import type { TPushNotification } from "./types";

/**
 * Stage 3b — route a parsed notification to its owning feature model.
 *
 * Scope boundary: this class owns routing only. Every branch is a one-line
 * handoff; no feature logic lives here.
 *
 * FUTURE SCOPE: add one `case` per newly supported notification type. The
 * compiler enforces this — widening the TPushNotification union without
 * adding a branch makes the `logUnhandled(data)` call in the default arm a
 * type error, so a notification can never be parsed and then silently
 * dropped.
 */
export class PushNotificationRouter {
  private static readonly TAG: string = "PushNotificationRouter";

  /**
   * Hand a notification to the model that owns its type.
   *
   * @param data - The parsed notification
   */
  public static route(data: TPushNotification): void {
    // A switch is required, not stylistic: it is what lets TypeScript narrow
    // the discriminant to `never` in the default arm, which is the
    // exhaustiveness guard that forces every future notification type to be
    // routed. It grows a case per type as the union widens.
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (data.notificationType) {
      case PushNotificationType.EMERGENCY_ALERT_DISPLAY:
        EamModelImpl.getInstance().onNotificationReceived(data);
        break;
      default:
        PushNotificationRouter.logUnhandled(data.notificationType, data);
        break;
    }
  }

  /**
   * Compile-time exhaustiveness guard and runtime fallback.
   *
   * The `never` type on the discriminant is what forces a new union member
   * to get a `case` in route(): once a type is added to
   * PushNotificationType without a branch, its literal survives into this
   * arm and no longer assigns to `never`. The body still logs, so an object
   * reaching this arm at runtime despite the types is reported rather than
   * silently swallowed.
   *
   * @param notificationType - The unhandled discriminant, narrowed to never
   * @param data - The unhandled notification, for logging
   */
  private static logUnhandled(
    notificationType: never,
    data: TPushNotification
  ): void {
    Log.w(
      PushNotificationRouter.TAG,
      `logUnhandled(): No route for notificationType=${String(notificationType)}, originRequestId=${data.originRequestId}`
    );
  }
}
